import { create } from "zustand";
import { Participant } from "../../types/participant/tableRow";
import { Transaction } from "../../types/transaction/tableRow";
import { Attendance } from "../../types/attendance/tableRow";
import { Direction } from "../../types/direction/tableRow";
import { Role } from "../../types/role/roleRow";

interface AppState {
  // Пользователь
  user: Role | null;
  isAuth: boolean;

  // Актуальные данные
  participants: Participant[];
  transactions: Transaction[];
  attendances: Attendance[];
  directions: Direction[];

  // Состояние загрузки
  isLoading: boolean;
  error: string | null;

  // Вкладка
  tab: string | null;
  participant: number | null;

  // Действия для обновления состояния
  setUser: (user: Role | null) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  setParticipants: (participants: Participant[]) => void;
  setTransactions: (transactions: Transaction[]) => void;
  setAttendances: (attendances: Attendance[]) => void;
  setDirections: (directions: Direction[]) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setTab: (tab: string | null) => void;
  setParticipant: (participant: number | null) => void;

  // Действия для добавления данных
  addParticipant: (participant: Participant) => void;
  addTransaction: (transaction: Transaction) => void;
  addAttendance: (attendance: Attendance) => void;
  addAttendances: (attendances: Attendance[]) => void;
  addDirection: (direction: Direction) => void;

  // Действия для удаления данных
  removeParticipant: (id: number) => void;
  removeTransaction: (id: number) => void;
  removeAttendance: (id: number) => void;
  removeDirection: (id: number) => void;

  // Действия для сброса состояния
  reset: () => void;
}

const initialState = {
  user: null,
  isAuth: false,
  participants: [],
  transactions: [],
  attendances: [],
  directions: [],

  tab: null,
  participant: null,

  isLoading: false,
  error: null,
};

export const useAppStore = create<AppState>((set) => ({
  ...initialState,

  setUser: (user: Role | null) => set({ user }),
  setAuthenticated: (isAuth: boolean) => set({ isAuth }),
  setParticipants: (participants: Participant[]) => set({ participants }),
  setTransactions: (transactions: Transaction[]) => set({ transactions }),
  setAttendances: (attendances: Attendance[]) => set({ attendances }),
  setDirections: (directions: Direction[]) => set({ directions }),
  setTab: (tab: string | null) => set({ tab }),
  setParticipant: (participant: number | null) => set({ participant }),
  setLoading: (isLoading: boolean) => set({ isLoading }),
  setError: (error: string | null) => set({ error }),

  addParticipant: (participant: Participant) =>
    set((state: AppState) => ({
      participants: [...state.participants, participant],
    })),

  addTransaction: (transaction: Transaction) =>
    set((state: AppState) => ({
      transactions: [...state.transactions, transaction],
    })),

  addAttendance: (attendance: Attendance) =>
    set((state: AppState) => ({
      attendances: [...state.attendances, attendance],
    })),

  addAttendances: (newAttendances: Attendance[]) =>
    set((state: AppState) => ({
      attendances: [...state.attendances, ...newAttendances],
    })),

  addDirection: (direction: Direction) =>
    set((state: AppState) => ({
      directions: [...state.directions, direction],
    })),

  removeParticipant: (id: number) =>
    set((state: AppState) => ({
      participants: state.participants.filter((p: Participant) => p.id !== id),
    })),

  removeTransaction: (id: number) =>
    set((state: AppState) => ({
      transactions: state.transactions.filter((t: Transaction) => t.id !== id),
    })),

  removeAttendance: (id: number) =>
    set((state: AppState) => ({
      attendances: state.attendances.filter((a: Attendance) => a.id !== id),
    })),

  removeDirection: (id: number) =>
    set((state: AppState) => ({
      directions: state.directions.filter((d: Direction) => d.id !== id),
    })),

  reset: () => set(initialState),
}));
